import { familyKey, familyFor, groupRecords } from '@theme/ts-product-families';
import { StandardEvents } from '@shopify/events';

const searchWords = value => normalizeOptionName(value).match(/[\p{L}\p{N}]+/gu) || [];
export function preferLiteralSearchMatches(records, query) {
  if (!query || /[:"()\\]|(?:^|\s)(?:AND|OR|NOT)(?:\s|$)|(?:^|\s)-\S/.test(query)) return records;
  const terms = searchWords(query);
  if (!terms.length) return records;
  const matches = records.filter(record => {
    const words = searchWords(record.searchText || [record.title, record.brand].join(' '));
    return terms.every(term => words.some(word => word === term || (term.length >= 3 && word.startsWith(term))));
  });
  return matches.length ? matches : records;
}

export const browseFacetNames = ['category', 'color', 'size', 'material', 'age'];
const normalizeOptionName = value => String(value).normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase().trim();
const normalizeBrand = value => String(value).normalize('NFC').toLocaleLowerCase().trim();
const optionAliases = {
  color: ['color', 'colour', 'farve', 'farg', 'farbe', 'couleur', 'kleur'],
  size: ['size', 'størrelse', 'storlek', 'große', 'grosse', 'taille', 'maat'],
  material: ['material', 'materiale', 'materiau', 'matiere', 'materiaal'],
  age: ['age', 'alder', 'alter', 'leeftijd']
};
export function browseVariantMatches(record, state) {
  if (state.query) {
    const haystack = normalizeOptionName([record.title, ...record.categories.map(category => category.label)].join(' '));
    if (!normalizeOptionName(state.query).split(/\s+/).every(word => haystack.includes(word))) return false;
  }
  if (state.brand && normalizeBrand(record.brand) !== normalizeBrand(state.brand)) return false;
  if (state.category && !record.categories.some(category => category.value === state.category)) return false;
  const hasVariantFilter = ['color', 'size', 'material', 'age'].some(name => state[name]);
  if (!record.variants.length) return !hasVariantFilter && (!state.stock || record.available) && (state.min === '' || record.price >= Number(state.min) * 100) && (state.max === '' || record.price <= Number(state.max) * 100);
  return record.variants.some(variant => (!state.stock || variant.available) &&
    (state.min === '' || variant.price >= Number(state.min) * 100) &&
    (state.max === '' || variant.price <= Number(state.max) * 100) &&
    ['color', 'size', 'material', 'age'].every(name => !state[name] || (record.optionIndexes[name] >= 0 && variant.options[record.optionIndexes[name]] === state[name])));
}
const readBrowseJSON = value => { try { return JSON.parse(value || '[]'); } catch { return []; } };

export class FamilySearch extends HTMLElement {
  connectedCallback() {
    this.addEventListener('ts:card-variant-changed', this.onVariant);
    this.addEventListener(StandardEvents.searchUpdate, this.onSearchUpdate);
    this.addEventListener('change', this.onControlChange);
    this.addEventListener('input', this.onControlInput);
    this.addEventListener('submit', this.onControlSubmit);
    this.addEventListener('click', this.onClear);
    this.addEventListener('click', this.onBrandCategory);
    this.refresh();
  }
  disconnectedCallback() {
    this.removeEventListener('ts:card-variant-changed', this.onVariant);
    this.removeEventListener(StandardEvents.searchUpdate, this.onSearchUpdate);
    this.removeEventListener('change', this.onControlChange);
    this.removeEventListener('input', this.onControlInput);
    this.removeEventListener('submit', this.onControlSubmit);
    this.removeEventListener('click', this.onClear);
    this.removeEventListener('click', this.onBrandCategory);
    this.controller?.abort();
  }
  refresh() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.selected = new Map();
    this.limit ||= Number(this.dataset.batch) || 24;
    this.load(this.controller.signal);
  }
  getBrowseState() { return { limit: this.limit }; }
  onSearchUpdate = async event => {
    const revision = this.renderRevision = (this.renderRevision || 0) + 1;
    this.controller?.abort();
    try {
      await event.promise;
      if (this.isConnected && this.renderRevision === revision) this.refresh();
    } catch {}
  };
  restoreBrowseState(state) {
    this.limit = Math.max(Number(this.dataset.batch) || 24, Math.min(10000, Number(state?.limit) || 24));
    if (this.complete) this.update();
  }
  onVariant = event => {
    const item = event.target.closest('.product-grid__item');
    const record = this.records?.find(record => record.node === item);
    if (!record || !this.complete) return;
    this.selected.set(record.key, record);
    this.update();
  };
  async load(signal) {
    const list = this.querySelector('results-list');
    const grid = list?.querySelector('.product-grid');
    if (!grid || !grid.hasAttribute('data-search-total')) return;
    this.complete = false;
    this.sort = list.dataset.familySort || this.dataset.sort;
    delete grid.dataset.familyComplete;
    delete grid.dataset.familyCount;
    this.more?.remove();
    const pages = Math.max(1, Number(grid.dataset.lastPage) || 1);
    const current = Number(grid.dataset.currentPage || list.querySelector('#ResultsList')?.dataset.page) || 1;
    const pagesOfItems = new Map([[current, [...grid.querySelectorAll(':scope > .product-grid__item')]]]);
    try {
      for (let page = 1; page <= pages; page++) {
        if (page === current) continue;
        const url = new URL(location.href);
        url.searchParams.set('page', String(page));
        url.searchParams.set('section_id', this.dataset.section);
        const response = await fetch(url, { credentials: 'same-origin', signal });
        if (!response.ok) throw new Error('page-unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = doc.querySelector('ts-family-search');
        const nextGrid = incoming?.querySelector('.product-grid');
        if (!nextGrid || incoming.dataset.section !== this.dataset.section || Number(nextGrid.dataset.lastPage) !== pages) throw new Error('wrong-search-page');
        pagesOfItems.set(page, [...nextGrid.querySelectorAll(':scope > .product-grid__item')].map(node => document.importNode(node, true)));
      }
      if (signal.aborted || !this.isConnected || this.querySelector('results-list .product-grid') !== grid) return;
      const seen = new Set();
      const items = [...pagesOfItems].sort(([a], [b]) => a - b).flatMap(([, items]) => items).filter(item => {
        const id = item.dataset.productId;
        if (!id || seen.has(id)) return false;
        seen.add(id); return true;
      });
      for (const item of items) if (item.parentElement !== grid) grid.append(item);
      this.grid = grid;
      this.records = items.map((node, order) => {
        const card = node.querySelector('[data-ts-product-card]');
        const id = node.dataset.productId;
        const family = familyFor(id, card?.dataset.tsHandle);
        node.dataset.familyKey = familyKey(id, card?.dataset.tsHandle);
        const optionNames = readBrowseJSON(card?.dataset.tsOptionNames).map(normalizeOptionName);
        const optionIndexes = Object.fromEntries(Object.entries(optionAliases).map(([name, aliases]) => [name, optionNames.findIndex(option => aliases.includes(option))]));
        return { id, node, order, searchText: node.dataset.searchText || '', key: node.dataset.familyKey, title: card?.dataset.tsTitle || family?.title || '', brand: card?.dataset.tsVendor || '', created: Number(card?.dataset.tsCreated) || 0, price: Number(card?.dataset.tsPrice), available: card?.dataset.tsAvailable === 'true', variants: readBrowseJSON(card?.dataset.tsBrowseVariants), categories: readBrowseJSON(card?.dataset.tsBrowseCategories), optionIndexes };
      });
      this.searchRecords = this.hasAttribute('data-search-page')
        ? preferLiteralSearchMatches(this.records, new URL(location.href).searchParams.get('q') || '')
        : this.records;
      this.complete = true;
      this.setupControls();
      list.querySelectorAll('nav.pagination, nav.ts-focused-collection__pagination').forEach(control => { control.hidden = true; });
      this.more = document.createElement('button');
      this.more.type = 'button';
      this.more.className = 'button-secondary ts-family-search__more';
      this.more.textContent = this.dataset.moreLabel;
      this.more.addEventListener('click', () => {
        const oldLimit = this.limit;
        this.limit += Number(this.dataset.batch) || 24;
        this.update();
        const target = this.display?.[oldLimit];
        target?.querySelector('a[href]')?.focus({ preventScroll: true });
        target?.scrollIntoView({ block: 'start', behavior: 'instant' });
      }, { signal });
      grid.after(this.more);
      this.update();
    } catch (error) {
      if (!signal.aborted && this.controller?.signal === signal && error.name !== 'AbortError') this.complete = false;
    }
  }
  setupControls() {
    const form = this.querySelector('[data-browser-controls]');
    if (!form) return;
    const current = new URL(location.href);
    const brandLabels = new Map();
    const brandCollator = new Intl.Collator(document.documentElement.lang || undefined, { caseFirst: 'upper' });
    const brandNames = this.searchRecords.map(record => record.brand).filter(Boolean).sort(brandCollator.compare);
    for (const brand of brandNames) {
      const key = normalizeBrand(brand);
      if (!brandLabels.has(key)) brandLabels.set(key, brand);
    }
    const brands = [...brandLabels.values()];
    const select = form.querySelector('[data-browser-brand]');
    const selectedBrand = select.value || current.searchParams.get('ts_brand') || '';
    select.querySelectorAll('option:not(:first-child)').forEach(option => option.remove());
    brands.forEach(brand => select.append(new Option(brand, brand)));
    select.value = brandLabels.get(normalizeBrand(selectedBrand)) || '';
    form.querySelectorAll('[data-browser-extra], [data-browser-custom]').forEach(element => { element.hidden = false; element.disabled = false; });
    const sort = form.querySelector('[data-browser-sort]');
    const sortValue = current.searchParams.get('ts_sort') || this.sort;
    if ([...sort.options].some(option => option.value === sortValue)) sort.value = sortValue;
    form.querySelector('[data-browser-stock]').checked = current.searchParams.get('ts_stock') === '1';
    form.querySelector('[data-browser-min]').value = current.searchParams.get('ts_min') || '';
    form.querySelector('[data-browser-max]').value = current.searchParams.get('ts_max') || '';
    const query = form.querySelector('[data-browser-query]');
    if (query) query.value = current.searchParams.get('ts_query') || '';
    for (const name of browseFacetNames) {
      const select = form.querySelector(`[data-browser-facet="${name}"]`);
      if (!select) continue;
      const previous = select.value || current.searchParams.get(`ts_${name}`) || '';
      const values = new Map();
      for (const record of this.searchRecords) {
        if (name === 'category') record.categories.forEach(category => values.set(category.value, category.label));
        else if (record.optionIndexes[name] >= 0) record.variants.forEach(variant => {
          const value = variant.options[record.optionIndexes[name]];
          if (value && value !== 'Default Title') values.set(value, value);
        });
      }
      select.querySelectorAll('option:not(:first-child)').forEach(option => option.remove());
      [...values].sort((a, b) => new Intl.Collator(document.documentElement.lang || undefined, { numeric: true }).compare(a[1], b[1])).forEach(([value, label]) => select.append(new Option(label, value)));
      select.value = values.has(previous) ? previous : '';
      select.closest('[data-browser-facet-wrapper]').hidden = values.size < 2;
    }
    const categoryNav = this.querySelector('[data-brand-categories]');
    if (categoryNav) {
      const categorySelect = form.querySelector('[data-browser-facet="category"]');
      categoryNav.replaceChildren();
      for (const option of categorySelect.options) {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.brandCategory = option.value;
        button.textContent = option.value ? option.textContent : categoryNav.dataset.allLabel;
        button.setAttribute('aria-pressed', String(option.value === categorySelect.value));
        categoryNav.append(button);
      }
      categoryNav.hidden = categorySelect.options.length < 2;
      if (!categoryNav.hidden) categorySelect.closest('[data-browser-facet-wrapper]').hidden = true;
    }
  }
  onBrandCategory = event => {
    const button = event.target.closest('[data-brand-category]');
    if (!button) return;
    this.querySelector('[data-browser-facet="category"]').value = button.dataset.brandCategory;
    this.limit = Number(this.dataset.batch) || 24;
    if (this.saveControls()) this.update();
  };
  readControls() {
    const form = this.querySelector('[data-browser-controls]');
    return {
      sort: form?.querySelector('[data-browser-sort]')?.value || this.sort,
      brand: form?.querySelector('[data-browser-brand]')?.value || '',
      stock: form?.querySelector('[data-browser-stock]')?.checked || false,
      min: form?.querySelector('[data-browser-min]')?.value || '',
      max: form?.querySelector('[data-browser-max]')?.value || '',
      query: form?.querySelector('[data-browser-query]')?.value || '',
      ...Object.fromEntries(browseFacetNames.map(name => [name, form?.querySelector(`[data-browser-facet="${name}"]`)?.value || '']))
    };
  }
  saveControls() {
    const state = this.readControls();
    const url = new URL(location.href);
    for (const [name,value] of Object.entries({ ts_brand:state.brand, ts_stock:state.stock?'1':'', ts_min:state.min, ts_max:state.max, ts_query:state.query, ts_sort:state.sort, ...Object.fromEntries(browseFacetNames.map(name => [`ts_${name}`, state[name]])) })) {
      if (value) url.searchParams.set(name,value); else url.searchParams.delete(name);
    }
    url.searchParams.delete('page');
    if (this.dataset.context === 'collection' && ['manual','best-selling'].includes(state.sort) && state.sort !== this.dataset.sort) {
      url.searchParams.set('sort_by',state.sort);
      location.assign(url.toString());
      return false;
    }
    history.replaceState(history.state,'',url);
    return true;
  }
  onControlInput = event => {
    if (event.target.matches('[data-browser-min], [data-browser-max], [data-browser-query]')) this.onControlChange(event);
  };
  onControlChange = event => {
    if (!event.target.closest('[data-browser-controls]')) return;
    if (!this.complete) { if (event.target.matches('[data-browser-sort]')) event.target.form.requestSubmit(); return; }
    this.limit = Number(this.dataset.batch) || 24;
    if (this.saveControls()) this.update();
  };
  onControlSubmit = event => {
    if (!this.complete || !event.target.matches('[data-browser-controls]')) return;
    event.preventDefault();
    if (this.saveControls()) this.update();
  };
  onClear = event => {
    if (!event.target.closest('[data-browser-clear]')) return;
    const form = this.querySelector('[data-browser-controls]');
    form.querySelector('[data-browser-brand]').value = '';
    form.querySelector('[data-browser-stock]').checked = false;
    form.querySelector('[data-browser-min]').value = '';
    form.querySelector('[data-browser-max]').value = '';
    const query = form.querySelector('[data-browser-query]');
    if (query) query.value = '';
    form.querySelectorAll('[data-browser-facet]').forEach(select => { select.value = ''; });
    this.limit = Number(this.dataset.batch) || 24;
    if (this.saveControls()) this.update();
  };
  update() {
    if (!this.complete || !this.grid?.isConnected) return;
    const sortNames = { 'price-ascending': 'price-asc', 'price-descending': 'price-desc' };
    const refreshed = this.records.map(record => ({ ...record, price: Number(record.node.querySelector('[data-ts-product-card]')?.dataset.tsPrice) }));
    const selected = new Map([...this.selected].map(([key, record]) => [key, refreshed.find(item => item.node === record.node)]));
    const state = this.readControls();
    const searchNodes = new Set(this.searchRecords.map(record => record.node));
    const eligible = refreshed.filter(record => searchNodes.has(record.node) && browseVariantMatches(record, state));
    const groups = groupRecords(eligible, selected, sortNames[state.sort || this.sort] || 'default');
    const collator = new Intl.Collator(document.documentElement.lang || undefined, { sensitivity: 'base', numeric: true });
    if (state.sort?.startsWith('brand-')) groups.sort((a,b) => (state.sort === 'brand-descending' ? -1 : 1) * (collator.compare(a.chosen.brand,b.chosen.brand) || collator.compare(a.chosen.title,b.chosen.title)));
    if (state.sort?.startsWith('title-')) groups.sort((a,b) => (state.sort === 'title-descending' ? -1 : 1) * collator.compare(a.chosen.title,b.chosen.title));
    if (state.sort?.startsWith('created-')) groups.sort((a,b) => (state.sort === 'created-ascending' ? 1 : -1) * (a.chosen.created-b.chosen.created));
    const count = this.querySelector('[data-browser-count]');
    if (count) count.textContent = (groups.length === 1 ? count.dataset.one : count.dataset.other).replace('[count]', String(groups.length));
    const empty = this.querySelector('[data-browser-empty]');
    if (empty) empty.hidden = groups.length > 0;
    const clear = this.querySelector('[data-browser-clear]');
    if (clear) clear.hidden = !state.brand && !state.stock && state.min === '' && state.max === '' && !state.query && !browseFacetNames.some(name => state[name]);
    this.querySelectorAll('[data-brand-category]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.brandCategory === state.category)));

    this.display = groups.map(group => group.chosen.node);
    const shown = this.display.slice(0, this.limit);
    shown.forEach((node, index) => { if (this.grid.children[index] !== node) this.grid.insertBefore(node, this.grid.children[index] || null); });
    const visible = new Set(shown);
    for (const record of this.records) record.node.hidden = !visible.has(record.node);
    this.more.hidden = this.limit >= groups.length;
    this.grid.dataset.familyComplete = 'true';
    this.grid.dataset.familyCount = String(groups.length);
    const summary = this.closest('#MainContent')?.querySelector('.ts-search-summary[data-count-template]');
    if (summary && summary.dataset.empty !== 'true') {
      const template = summary.dataset.countTemplate;
      const marker = '[count]';
      const index = template.lastIndexOf(marker);
      if (index >= 0) {
        const message = template.slice(0, index) + String(groups.length) + template.slice(index + marker.length);
        if (summary.textContent.trim() !== message) summary.textContent = message;
        if (this.dataset.shopName) document.title = `${message} · ${this.dataset.shopName}`;
      }
    }
    this.grid.dispatchEvent(new Event('ts:family-updated', { bubbles: true }));
  }
}

if (!customElements.get('ts-family-search')) customElements.define('ts-family-search', FamilySearch);
